// src/pages/partners/MyPartnerPriceList.tsx
//
// MyPartnerPriceList (Partner-only view)
//
// Overview
// - Displays the list of products that the signed-in partner can sell, including supply price and sale type.
// - Search UI is provided via MyPartnerPriceListSearchPanel (shared SearchPanel abstraction).
//
// Search / URL policy
// - Uses "draft vs applied" search to avoid filtering while typing.
// - The URL query string is the single source of truth for the applied state (deep links + back/forward supported).
// - This page must NOT implement a local debounce; it must delegate auto-search behavior to SearchPanel.
//
// Refactor in this change
// - Standardize the table with shared table components:
//   - StandardTableToolbar + StandardTableFrame + StandardPagination
// - Keeps a wide, horizontally-scrollable table layout consistent with partner-facing pages.
//
// Notes
// - This page is intended for Partner accounts. For non-partner sessions, it shows an informational alert.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Divider, TableCell, TableRow, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { utcToZonedTime } from 'date-fns-tz';

import { userState } from '@recoils/atoms';
import { UserGroup } from '@models/user';

import { usePartnerDetail } from '@queries/partners/usePartnerDetail';
import { useActivePartnerProductPrices } from '@queries/partners/useActivePartnerProductPrices';
import { useActivePartnerProductPriceCount } from '@queries/partners/useActivePartnerProductPriceCount';
import { useCountryList } from '@queries/countries/useCountryList';

import { exportActivePartnerPricesExcel } from '@apis/partner';

import type { Partner, PartnerProductPriceResponse } from '@models/partner';
import { ProductSaleType, type GetProductsParams } from '@apis/product';

import { toUsdDisplay } from '@utils/money';
import { downloadBlob } from '@utils/file';
import { filterSearchParams } from '@utils/search';
import { toDisplayPartnerId } from './util';
import { KOREAN_TIME_ZONE } from '@constants/timezone';
import { PRODUCT_SALE_TYPE_LABELS } from '@utils/productLabels';

import { usePageHeader } from '@components/common/pageHeader.context';
import type { SetSearchValueFn } from '@components/common/search';

import MyPartnerPriceListSearchPanel from '@components/partner/MyPartnerPriceListSearchPanel';

import { StandardPagination, StandardTableFrame, StandardTableToolbar } from '@components/common/table';

type ProductSearchForm = GetProductsParams;
type Option<T> = { label: string; value: T };

const PAGE_SIZE = 50;

/**
 * Page-level auto-search policy
 * - Global default autoSearch is OFF (SearchPanel-level policy).
 * - This page explicitly opts out by default.
 *
 * IMPORTANT:
 * - Keep the object shape stable to avoid TS literal inference issues.
 * - Do NOT conditionally omit this object.
 */
const PAGE_AUTO_SEARCH = { enabled: false, delayMs: 500 } as const;

const EMPTY_PARTNER: Partner = {
  name: '',
  partnerId: 0,
  managerUsername: '',
  managerPassword: '',
  managerEmail: '',
  managerName: '',
  managerTelephone: '',
  address: '',
  balance: 0,
  baseDiscountRate: 0,
  callbackUrl: '',
  createdAt: '',
  appId: '',
  appSecret: '',
  remoteIps: [],
  manualOrderEnabled: false,
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const sortByLabel = <T,>(a: Option<T>, b: Option<T>) =>
  a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

const parsePositiveInt = (value: string | null, fallback = 1) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const normalizeText = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

const isValidProductSaleType = (v: string | null): v is ProductSaleType => {
  if (!v) return false;
  return (Object.values(ProductSaleType) as string[]).includes(v);
};

/* ------------------------------------------------------------------ */
/* URL <-> state helpers                                               */
/* ------------------------------------------------------------------ */

const readSearchFromUrl = (sp: URLSearchParams): ProductSearchForm => {
  const saleTypeRaw = sp.get('productSaleType');

  return {
    productName: sp.get('productName') || '',
    countryId: sp.get('countryId') ? Number(sp.get('countryId')) : undefined,
    productSaleType: isValidProductSaleType(saleTypeRaw) ? saleTypeRaw : undefined,
  };
};

const cleanSearchForm = (form: ProductSearchForm): ProductSearchForm => {
  const name = normalizeText(form.productName);

  return {
    ...form,
    productName: name,
    countryId: typeof form.countryId === 'number' ? form.countryId : undefined,
    productSaleType: form.productSaleType || undefined,
  };
};

const buildUrlParams = (form: ProductSearchForm, page: number) => {
  const cleaned = cleanSearchForm(form);
  const params: Record<string, string> = {};

  if (cleaned.productName) params.productName = cleaned.productName;
  if (typeof cleaned.countryId === 'number') params.countryId = String(cleaned.countryId);
  if (cleaned.productSaleType) params.productSaleType = String(cleaned.productSaleType);

  return {
    ...filterSearchParams(params),
    page: String(page),
  };
};

const toSafeFileName = (raw: string) =>
  raw
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const MyPartnerPriceList = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const user = useRecoilValue(userState);
  const isPartnerUser = user?.group === UserGroup.PARTNER;
  const partnerId = isPartnerUser ? Number(user?.partnerId) : 0;

  const { setPageHeaderSlots } = usePageHeader();

  /* ---------------- Partner header ---------------- */

  const [partner, setPartner] = useState<Partner>(EMPTY_PARTNER);
  const { data: partnerDetailForm } = usePartnerDetail(partnerId);

  useEffect(() => {
    const loaded = partnerDetailForm?.data?.[0];
    if (!loaded) return;
    setPartner(loaded);
  }, [partnerDetailForm]);

  const headerTitle = useMemo(() => {
    const name = partner.name || 'Partner';
    const displayId = toDisplayPartnerId(partner.partnerId);
    return `${name} (${displayId})`;
  }, [partner.name, partner.partnerId]);

  useEffect(() => {
    setPageHeaderSlots({ leading: { title: headerTitle } });
  }, [setPageHeaderSlots, headerTitle]);

  /* ---------------- Search state (draft vs applied) ---------------- */

  const [appliedSearch, setAppliedSearch] = useState<ProductSearchForm>(() => readSearchFromUrl(searchParams));
  const [draftSearch, setDraftSearch] = useState<ProductSearchForm>(() => ({ ...appliedSearch }));
  const [page, setPage] = useState<number>(() => parsePositiveInt(searchParams.get('page'), 1));
  const [filtersOpen, setFiltersOpen] = useState(false);

  /**
   * Re-hydrate state when URL changes (deep link / back-forward / manual URL edit).
   * The URL represents the applied state only.
   */
  useEffect(() => {
    const nextApplied = readSearchFromUrl(searchParams);
    const nextPage = parsePositiveInt(searchParams.get('page'), 1);

    setAppliedSearch(nextApplied);

    setDraftSearch((prev) => {
      const prevClean = cleanSearchForm(prev);
      const nextClean = cleanSearchForm(nextApplied);

      const same =
        (prevClean.productName ?? '') === (nextClean.productName ?? '') &&
        (prevClean.countryId ?? null) === (nextClean.countryId ?? null) &&
        (prevClean.productSaleType ?? null) === (nextClean.productSaleType ?? null);

      return same ? prev : nextApplied;
    });

    setPage(nextPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /* ---------------- Queries (driven by appliedSearch + page) ---------------- */

  const { data: partnerProductPrices, isLoading: isLoadingPrices } = useActivePartnerProductPrices({
    partnerId,
    page,
    pageSize: PAGE_SIZE,
    searchParam: appliedSearch,
  });

  const { data: partnerProductPriceCount, isLoading: isLoadingCount } = useActivePartnerProductPriceCount({
    partnerId,
    searchParam: appliedSearch,
  });

  const totalCount = partnerProductPriceCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const isTableLoading = Boolean(isLoadingPrices || isLoadingCount);

  const prices: PartnerProductPriceResponse[] = useMemo(() => partnerProductPrices?.data ?? [], [partnerProductPrices]);

  /* ---------------- Options ---------------- */

  const { data: countriesData } = useCountryList();

  const countryOptions: Option<number>[] = useMemo(() => {
    const raw = countriesData?.data ?? [];
    return raw.map((c) => ({ label: c.englishName, value: c.countryId as number })).sort(sortByLabel);
  }, [countriesData]);

  const saleTypeOptions: Option<ProductSaleType>[] = useMemo(() => {
    const values = Object.values(ProductSaleType) as ProductSaleType[];
    return values.map((v) => ({ value: v, label: PRODUCT_SALE_TYPE_LABELS?.[v] ?? String(v) })).sort(sortByLabel);
  }, []);

  const selectedCountry = useMemo(() => {
    if (!draftSearch.countryId) return null;
    return countryOptions.find((o) => o.value === draftSearch.countryId) ?? null;
  }, [countryOptions, draftSearch.countryId]);

  const selectedSaleType = useMemo(() => {
    if (!draftSearch.productSaleType) return null;
    return saleTypeOptions.find((o) => o.value === draftSearch.productSaleType) ?? null;
  }, [saleTypeOptions, draftSearch.productSaleType]);

  /* ---------------- Apply helpers (URL is the source of truth) ---------------- */

  const syncUrlFromApplied = useCallback(
    (nextApplied: ProductSearchForm, nextPage: number) => {
      setSearchParams(buildUrlParams(nextApplied, nextPage));
    },
    [setSearchParams],
  );

  const handleSearch = useCallback(() => {
    const nextApplied = cleanSearchForm(draftSearch);
    setAppliedSearch(nextApplied);
    setPage(1);
    syncUrlFromApplied(nextApplied, 1);
  }, [draftSearch, syncUrlFromApplied]);

  const handleReset = useCallback(() => {
    const cleared: ProductSearchForm = {
      productName: '',
      countryId: undefined,
      productSaleType: undefined,
    };

    setDraftSearch(cleared);

    const nextApplied = cleanSearchForm(cleared);
    setAppliedSearch(nextApplied);
    setPage(1);
    syncUrlFromApplied(nextApplied, 1);
  }, [syncUrlFromApplied]);

  /* ---------------- Page change ---------------- */

  const handleChangePage = (next: number) => {
    const safeNext = clamp(next, 1, totalPages);
    setPage(safeNext);
    syncUrlFromApplied(appliedSearch, safeNext);
  };

  /* ---------------- Export (based on appliedSearch) ---------------- */

  const [isExporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (isExporting) return;

    try {
      setExporting(true);

      const nowUtc = new Date();
      const kstDate = utcToZonedTime(nowUtc, KOREAN_TIME_ZONE);

      const res = await exportActivePartnerPricesExcel(partnerId, appliedSearch);

      const safeName = toSafeFileName(partner.name || 'partner');
      const ts = kstDate.toISOString().replace(/[:]/g, '-');
      const fileName = `partner-${safeName}-prices-${ts}.xlsx`;

      downloadBlob(res, fileName);
    } finally {
      setExporting(false);
    }
  }, [appliedSearch, isExporting, partner.name, partnerId]);

  /* ---------------- setValue for shared chips/inputs ---------------- */

  const setValue: SetSearchValueFn<ProductSearchForm> = useCallback((key, value) => {
    setDraftSearch((prev) => {
      const next = { ...prev } as Record<string, unknown>;

      // Text fields use "" to keep controlled inputs stable; selects use undefined to omit from URL.
      if (key === 'productName') {
        next[key] = (value ?? '') as string;
      } else {
        next[key] = (value ?? undefined) as unknown;
      }

      return next as ProductSearchForm;
    });
  }, []);

  /**
   * Table columns:
   * - Product (code + name)
   * - Supply Price (USD)
   * - Sale Type
   */
  const columnsCount = 3;

  /**
   * Stable row key policy:
   * - Prefer productId when available.
   * - Otherwise fall back to productCode (unique in practice).
   */
  const getRowKey = useCallback((row: PartnerProductPriceResponse) => {
    const id = (row as any).productId;
    const code = (row as any).productCode;
    return String(id ?? code ?? '');
  }, []);

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {!isPartnerUser ? (
        <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
          This page is intended for Partner accounts. Some sections may not load if you are not signed in as a Partner.
        </Alert>
      ) : null}

      <MyPartnerPriceListSearchPanel
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        draftSearch={draftSearch}
        setValue={setValue}
        countryOptions={countryOptions}
        saleTypeOptions={saleTypeOptions}
        selectedCountry={selectedCountry}
        selectedSaleType={selectedSaleType}
        totalCount={totalCount}
        onSearch={handleSearch}
        onReset={handleReset}
        onExport={handleExport}
        exportDisabled={partnerId <= 0}
        exportLoading={isExporting}
        autoSearch={PAGE_AUTO_SEARCH}
      />

      <Divider />

      {/* Standard toolbar */}
      <Box mt={2}>
        <StandardTableToolbar
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={totalCount}
          onChangePage={handleChangePage}
          showTotalCount
          showPageJump
        />
      </Box>

      {/* Standard table */}
      <Box mt={1}>
        <StandardTableFrame<PartnerProductPriceResponse>
          containerSx={{
            borderRadius: 2,
            overflowX: 'auto',
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
          tableSx={{ minWidth: 960 }}
          columnsCount={columnsCount}
          isLoading={isTableLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText="No products found."
          head={
            <TableRow>
              <TableCell sx={{ minWidth: 360 }}>Product</TableCell>
              <TableCell align="right">Supply Price (USD)</TableCell>
              <TableCell align="center">Sale Type</TableCell>
            </TableRow>
          }
          headSx={(theme) => ({
            '& .MuiTableCell-head': {
              backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[900] : theme.palette.grey[800],
              color: theme.palette.common.white,
              fontWeight: 700,
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              whiteSpace: 'nowrap',
            },
          })}
          rows={prices}
          getRowKey={getRowKey}
          renderRow={(p) => {
            const rowAny = p as any;

            const effectiveDiscount =
              typeof rowAny.discountRate === 'number' && rowAny.discountRate !== 0
                ? rowAny.discountRate
                : partner.baseDiscountRate ?? 0;

            const originalPrice = Number(rowAny.originalPrice ?? 0);
            const supplyPrice = originalPrice * (1 - effectiveDiscount);

            const saleType = rowAny.productSaleType as ProductSaleType;
            const saleTypeLabel = PRODUCT_SALE_TYPE_LABELS?.[saleType] ?? String(saleType ?? '-');

            return (
              <>
                <TableCell sx={{ minWidth: 360 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                    {rowAny.productCode || '-'}
                  </Typography>
                  <Typography variant="body2" fontWeight={800} sx={{ lineHeight: 1.25, wordBreak: 'break-word' }}>
                    {rowAny.productName || '-'}
                  </Typography>
                </TableCell>

                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {toUsdDisplay(supplyPrice)}
                </TableCell>

                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                  {saleTypeLabel}
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      {/* Standard pagination */}
      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={totalCount}
          onChangePage={handleChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default MyPartnerPriceList;
